import React, { useState, useEffect } from 'react'
import { Box, Typography, Tooltip } from '@mui/material';
import supabase from '../../../../supabaseClient';
import { applyRoundingRules, findMatchingRule } from '../../../../Utils/roundingRules';

const HOURS_COLOR = '#A84355';
const PAYMENTS_COLOR = '#059669';

const MONTH_NUMBERS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

const pad = (n) => String(n).padStart(2, '0');
const localDateStr = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function buildEmptyMonthBuckets() {
    const now = new Date();
    const buckets = [];
    for (let i = 11; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        buckets.push({
            year: d.getFullYear(),
            month: d.getMonth(),
            label: `${MONTH_NUMBERS[d.getMonth()]}/${String(d.getFullYear()).slice(-2)}`,
            hours: 0,
            payments: 0,
        });
    }
    return buckets;
}

const formatHours = (h) => `${Math.round(h)}h`;

const formatPln = (v) => `${(v / 1000).toFixed(0)} tys.`;

const formatPlnFull = (v) =>
    `${v.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł`;

function StatBlock({ label, value }) {
    return (
        <Box sx={{ textAlign: 'right' }}>
            <Typography
                sx={{
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    letterSpacing: '0.5px',
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                }}
            >
                {label}
            </Typography>
            <Typography
                sx={{
                    fontSize: '1.15rem',
                    fontWeight: 700,
                    color: 'primary.main',
                    lineHeight: 1.3,
                }}
            >
                {value}
            </Typography>
        </Box>
    );
}

function LegendDot({ color, label }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: color }} />
            <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{label}</Typography>
        </Box>
    );
}

function MonthColumn({ data, maxHours, maxPayments, isCurrent }) {
    const BAR_MAX_HEIGHT = 60;
    const hoursHeight = Math.max((data.hours / maxHours) * BAR_MAX_HEIGHT, 6);
    const paymentsHeight = Math.max((data.payments / maxPayments) * BAR_MAX_HEIGHT, 6);

    return (
        <Tooltip
            arrow
            title={`${data.label}: ${formatHours(data.hours)} · ${formatPlnFull(data.payments)}`}
        >
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    borderRadius: '10px',
                    py: 1,
                    px: 0.5,
                    backgroundColor: isCurrent ? 'secondary.light' : 'transparent',
                    transition: 'background-color 0.15s ease',
                    cursor: 'default',
                    '&:hover': {
                        backgroundColor: isCurrent ? 'secondary.light' : 'action.hover',
                    },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        gap: '4px',
                        height: BAR_MAX_HEIGHT,
                    }}
                >
                    <Box
                        sx={{
                            width: 14,
                            height: hoursHeight,
                            backgroundColor: HOURS_COLOR,
                            borderRadius: '4px 4px 0 0',
                        }}
                    />
                    <Box
                        sx={{
                            width: 14,
                            height: paymentsHeight,
                            backgroundColor: PAYMENTS_COLOR,
                            borderRadius: '4px 4px 0 0',
                        }}
                    />
                </Box>

                <Typography
                    sx={{
                        mt: 1,
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        color: 'text.primary',
                    }}
                >
                    {data.label}
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.4 }}>
                    {formatHours(data.hours)}
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.4 }}>
                    {formatPln(data.payments)}
                </Typography>
            </Box>
        </Tooltip>
    );
}

function Last12MonthPayments({ idConfig }) {
    const [data, setData] = useState(buildEmptyMonthBuckets());
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            if (!idConfig) return;
            setLoading(true);

            const buckets = buildEmptyMonthBuckets();
            const bucketByKey = {};
            buckets.forEach((b) => { bucketByKey[`${b.year}-${b.month}`] = b; });

            const rangeFrom = new Date(buckets[0].year, buckets[0].month, 1);
            const now = new Date();
            const rangeTo = new Date(now.getFullYear(), now.getMonth() + 1, 0);

            const [
                { data: employeesData, error: employeesError },
                { data: attendanceData, error: attendanceError },
                { data: paymentsData, error: paymentsError },
                { data: rulesData, error: rulesError },
            ] = await Promise.all([
                supabase
                    .from('employees')
                    .select('id')
                    .eq('id_configuration', idConfig)
                    .is('archived', null),
                supabase
                    .from('attendance')
                    .select('check_in, check_out, employee_id')
                    .eq('id_configuration', idConfig)
                    .gte('check_in', `${localDateStr(rangeFrom)}T00:00:00`)
                    .lte('check_in', `${localDateStr(rangeTo)}T23:59:59`)
                    .not('check_in', 'is', null)
                    .not('check_out', 'is', null),
                supabase
                    .from('payments')
                    .select('amount, date, period_month, employee_id')
                    .eq('id_configuration', idConfig),
                supabase
                    .from('rounding_rules')
                    .select('*')
                    .eq('id_configuration', idConfig)
                    .eq('is_active', true),
            ]);

            const activeEmployeeIds = !employeesError
                ? new Set((employeesData || []).map((e) => e.id))
                : null;
            const rules = rulesError ? [] : (rulesData || []);

            if (!attendanceError) {
                (attendanceData || []).forEach((r) => {
                    if (activeEmployeeIds && !activeEmployeeIds.has(r.employee_id)) return;
                    const ci = new Date(r.check_in);
                    const bucket = bucketByKey[`${ci.getFullYear()}-${ci.getMonth()}`];
                    if (!bucket) return;
                    const matchedRule = findMatchingRule(r.check_in, rules);
                    const minutes = matchedRule
                        ? applyRoundingRules(r.check_in, r.check_out, matchedRule).billableMinutes
                        : Math.max(0, (new Date(r.check_out) - ci) / 60000);
                    bucket.hours += minutes / 60;
                });
            }

            if (!paymentsError) {
                (paymentsData || []).forEach((p) => {
                    if (activeEmployeeIds && !activeEmployeeIds.has(p.employee_id)) return;
                    // Bucket by the period the payment settles, not the day it was made.
                    const d = new Date(p.period_month || p.date);
                    const bucket = bucketByKey[`${d.getFullYear()}-${d.getMonth()}`];
                    if (bucket) bucket.payments += p.amount || 0;
                });
            }

            setData(buckets);
            setLoading(false);
        };

        fetchData();
    }, [idConfig]);

    const totalHours = data.reduce((sum, d) => sum + d.hours, 0);
    const totalPayments = data.reduce((sum, d) => sum + d.payments, 0);
    const avgPayments = totalPayments / data.length;

    const maxHours = Math.max(1, ...data.map((d) => d.hours));
    const maxPayments = Math.max(1, ...data.map((d) => d.payments));

    return (
        <Box
            sx={{
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'secondary.light',
                borderRadius: '16px',
                p: 2,
                opacity: loading ? 0.6 : 1,
                transition: 'opacity 0.15s ease',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 1,
                }}
            >
                <Box>
                    <Typography
                        sx={{
                            fontFamily: '"Roboto Condensed", sans-serif',
                            fontSize: '1.15rem',
                            fontWeight: 700,
                            color: 'primary.main',
                        }}
                    >
                        Ostatnie 12 miesięcy
                    </Typography>
                    <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                        Wszyscy pracownicy · godziny i wypłaty miesiąc po miesiącu
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <StatBlock label="Godziny / 12 mies." value={`${Math.round(totalHours).toLocaleString('pl-PL')} h`} />
                    <StatBlock label="Wypłacono" value={formatPlnFull(totalPayments)} />
                    <StatBlock label="Średnia / mies." value={formatPlnFull(avgPayments)} />

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <LegendDot color={HOURS_COLOR} label="godziny" />
                        <LegendDot color={PAYMENTS_COLOR} label="wypłaty" />
                    </Box>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                {data.map((d, i) => (
                    <MonthColumn
                        key={d.label}
                        data={d}
                        maxHours={maxHours}
                        maxPayments={maxPayments}
                        isCurrent={i === data.length - 1}
                    />
                ))}
            </Box>
        </Box>
    );
}

export default Last12MonthPayments;
